// Query-key factory — the single source of truth for the keys that take part
// in an INVALIDATION contract (a `useQuery` producer that some component
// `invalidateQueries`-es to force a refresh).
//
// WHY THIS EXISTS (BUILD-PLAN-COHERENCE PR-1, MA-35 — follows MA-31/MA-34).
// MA-31 fixed a class of "ghost invalidations": a component invalidated a key
// (e.g. `["account"]`) that NO `useQuery` ever produced, so TanStack silently
// no-op'd it. `scripts/audit/queryKeyConsistency.mjs` now fails CI if any
// invalidation's FIRST TOKEN has no producer. But that guard only checks the
// first token — it cannot see that a producer key
// `["engine-questions", skillId, childId, ...]` and a hand-typed variant with
// two args transposed are DIFFERENT cache entries (a silent "cache fork":
// two keys for the same data -> stale UI). Because the kid/parent surfaces are
// auth-gated, a cache fork is invisible on the Vercel preview, so we make it
// impossible at the source: each invalidation-contract key is defined ONCE
// here, and `scripts/audit/queryKeysFactoryGuard.mjs` bans these tokens from
// appearing as inline `queryKey` literals anywhere else.
//
// SCOPE — deliberately ONLY the keys that are invalidated. The ghost /
// cache-fork bug class lives entirely on the invalidation side: a producer-only
// key (e.g. ["story", slug]) has no consumer to drift against — it refetches
// purely by key-change, which `tsc` + render already cover. Centralizing
// those would be churn with cache-fork exposure and no bug payoff, so they
// stay inline by design. See docs/adr/014-query-key-factory.md.
//
// BYTE-IDENTITY CONTRACT. Every builder below reproduces the EXACT string
// prefix and argument order of the literal it replaced. `.all` is the
// prefix-only tuple used by `invalidateQueries` (a 1-element array, identical
// to the old `["todays-mission"]` etc.); `.for(...)` is the full producer key,
// built from the same prefix const so the full key and the invalidation prefix
// can never drift apart. The arg order is locked by
// src/lib/engine/__tests__/queryKeys.test.mjs, which asserts each builder
// emits its expected tuple verbatim.

// Prefix string defined once per key so `.all` and `.for(...)` share it.
const TODAYS_MISSION = "todays-mission";
const TRAIL_ATTEMPTS = "trail-attempts";
const TRAIL_MILESTONES = "trail-milestones";
const PARENT_OVERVIEW = "parent-overview";
const ENGINE_QUESTIONS = "engine-questions";
// MA-1202 RED r1 S-1 — the shared tier history sits UNDER the tree's
// ["skill-graph"] prefix (not a factory-owned token: inline ["skill-graph"]
// keys stay legal), so SessionRecap's ["skill-graph"] invalidation reaches it.
const SKILL_GRAPH = "skill-graph";
const TIER_HISTORY = "tier-history";

type MaybeId = string | null | undefined;

export const qk = {
  /** producer: useTodaysMission.ts -> ["todays-mission", kidId, localChildId, selectionAge].
   *  MA-771: the age slot is the RESOLVED selection age
   *  (resolveSelectionAge(progress.studentAge, registry age), MA-80) — not
   *  the registry's activeChildAge, which is only its fallback. */
  todaysMission: {
    all: [TODAYS_MISSION] as const,
    for: (kidId: MaybeId, localChildId: MaybeId, selectionAge: number | null) =>
      [TODAYS_MISSION, kidId, localChildId, selectionAge] as const,
  },
  /** producer: PathHome.tsx useTrailAttempts -> ["trail-attempts", kidId, localChildId] */
  trailAttempts: {
    all: [TRAIL_ATTEMPTS] as const,
    for: (kidId: MaybeId, localChildId: MaybeId) =>
      [TRAIL_ATTEMPTS, kidId, localChildId] as const,
  },
  /** producer: PathHome.tsx useTrailMilestones -> ["trail-milestones", kidId, localChildId] */
  trailMilestones: {
    all: [TRAIL_MILESTONES] as const,
    for: (kidId: MaybeId, localChildId: MaybeId) =>
      [TRAIL_MILESTONES, kidId, localChildId] as const,
  },
  /** producer: useOverviewData.ts ->
   *  ["parent-overview", childId, localChildId, shadowComponent]
   *
   *  MA-559 slice c (design D8): `shadowComponent` is a TRAILING addition —
   *  the read-union's shadow id when the parent has confirmed classroom
   *  history for this child ("added" latch + a family-shadow-map entry),
   *  else null. Arity stays constant across every latch state (null is the
   *  placeholder when the union is off), so a latch flip is a plain
   *  value-change on an existing key shape, not a new key shape. This is a
   *  DELIBERATE contract change over the pre-slice-c 3-element key — the
   *  existing byte-identity assertions in queryKeys.test.mjs are updated to
   *  the new 4-element array, not left pinned to the old one. */
  parentOverview: {
    all: [PARENT_OVERVIEW] as const,
    for: (childId: MaybeId, localChildId: MaybeId, shadowComponent: MaybeId = null) =>
      [PARENT_OVERVIEW, childId, localChildId, shadowComponent] as const,
  },
  /** producer: useEngineQuestions.ts -> ["engine-questions", skillId, childId, count, audience, sessionSeed] */
  engineQuestions: {
    all: [ENGINE_QUESTIONS] as const,
    // MA-303b RED round 2 (NIT 4) — `allowTransferForceServe` is now part
    // of the key. RED's qk.mjs (real @tanstack/query-core) showed that
    // when the focal and review hooks in useEngineQuestionsForRun share
    // one key (reviewSkillId === focalSkillId, e.g. MathSession's initial
    // null-review state), the FIRST observer's queryFn wins and persists
    // across refetches — order-dependent by construction, correct only
    // because focal (allow=true) happens to be declared first today.
    // Threading the flag into the key gives allow=true and allow=false
    // callers DISTINCT cache entries, so which one "wins" can no longer
    // depend on declaration order.
    for: (
      skillId: string,
      childId: string | null,
      count: number,
      audience: string,
      sessionSeed?: string,
      allowTransferForceServe?: boolean,
    ) => [ENGINE_QUESTIONS, skillId, childId, count, audience, sessionSeed, allowTransferForceServe] as const,
  },
  /** producer: attemptLogger.tierHistoryReader (queryClient.fetchQuery) ->
   *  ["skill-graph", "tier-history", childId]. MA-1202 RED r1 S-1: ONE full
   *  tier-history read per child id, shared by the tree, Where-now,
   *  MasteredOverTimeCard, the parent overview (per union id), the badges and
   *  the teacher class states. Invalidated by ["skill-graph"]. */
  tierHistory: {
    all: [SKILL_GRAPH, TIER_HISTORY] as const,
    for: (childId: string) => [SKILL_GRAPH, TIER_HISTORY, childId] as const,
  },
  /** producer: useStrandServed.useStrandTierRows -> ["skill-graph", "strand-tier-rows", kidId, localChildId].
   *  MA-1165 (D-1103-40): the parent tree's full-history row set
   *  (useSkillGraph.assembleTreeAttemptRows over the shared tierHistory read)
   *  for the per-strand pick on /play, the tree and Where-now. Under the
   *  ["skill-graph"] prefix, so SessionRecap's invalidation reaches it. */
  strandTierRows: {
    for: (kidId: MaybeId, localChildId: MaybeId) => [SKILL_GRAPH, "strand-tier-rows", kidId, localChildId] as const,
  },
} as const;

/** The prefix tokens this factory owns — consumed by the centralization guard
 *  so the ban list and the factory can never disagree. */
export const QUERY_KEY_FACTORY_TOKENS = [
  TODAYS_MISSION,
  TRAIL_ATTEMPTS,
  TRAIL_MILESTONES,
  PARENT_OVERVIEW,
  ENGINE_QUESTIONS,
] as const;
